/**
 * Announcement rotator. One message visible at a time, swapping every 7s.
 *
 * Why: the bar rendered every block side by side, so "Order ahead" and
 * "Free shipping $50+" competed in one 32px strip and truncated on tablet.
 * Rotation gives each message the whole line.
 *
 * Behavior contract:
 * - No-op with fewer than two messages (also true with JS off: CSS shows
 *   only the first message when the rotator flag is present, all of them
 *   otherwise, so a script failure can never blank the bar).
 * - Pauses on hover and on focus-within: nobody loses a link they were
 *   about to click.
 * - prefers-reduced-motion swaps instantly (visibility, no transition).
 * - The container is deliberately NOT aria-live: a rotating promo that
 *   announces itself every few seconds is screen-reader hostility, not
 *   accessibility. Hidden messages are display:none, so AT reads exactly
 *   one at any time.
 */
class AnnouncementRotator extends HTMLElement {
  static INTERVAL_MS = 7000;

  connectedCallback() {
    this.messages = [...this.querySelectorAll('.announcement-bar__message')];
    if (this.messages.length < 2) return;
    this.index = 0;
    this.paused = false;
    /* is-ready flips the CSS from "show first only" (the no-JS state)
       to "element drives visibility via [hidden]". */
    this.classList.add('is-ready');
    this.show(0);

    this.addEventListener('mouseenter', () => { this.paused = true; });
    this.addEventListener('mouseleave', () => { this.paused = false; });
    this.addEventListener('focusin', () => { this.paused = true; });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.paused = false;
    });

    this.timer = setInterval(() => {
      if (this.paused) return;
      this.show((this.index + 1) % this.messages.length);
    }, AnnouncementRotator.INTERVAL_MS);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  show(i) {
    this.index = i;
    this.messages.forEach((m, n) => {
      m.hidden = n !== i;
    });
  }
}

if (!customElements.get('announcement-rotator')) {
  customElements.define('announcement-rotator', AnnouncementRotator);
}
